import { Dispatch, SetStateAction, ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";

import {
  AccountBoxOutlined,
  ArticleOutlined,
  BusinessOutlined,
  ChevronLeft,
  ChevronRightOutlined,
  DashboardOutlined,
  FolderOutlined,
  HelpCenterOutlined,
} from "@mui/icons-material";

import FlexBetween from "@shared/components/FlexBetween";
import { useAppSelector } from "@store/hooks";
import { selectCurrentUserOrgs } from "@store/slices/orgsSlice";

interface Props {
  isNonMobile: boolean;
  drawerWidth: string;
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
}

// NavItem.icon == null marks a section header (renders as a label,
// not a clickable row). Path is the route the item navigates to.
type NavItem = { text: string; icon: ReactNode | null; path: string | null };

const fixedItems: NavItem[] = [
  { text: "Profile", icon: <AccountBoxOutlined />, path: "/profile" },
  { text: "Workspaces", icon: <ArticleOutlined />, path: "/workspaces" },
  { text: "Files", icon: <FolderOutlined />, path: "/files" },
  { text: "Learn", icon: <HelpCenterOutlined />, path: "/learn" },
];

export const AdminSidebar = ({
  isNonMobile,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
}: Props) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const orgs = useAppSelector(selectCurrentUserOrgs);

  useEffect(() => {
    setActive(pathname);
  }, [pathname]);

  const navItems: NavItem[] = [
    ...fixedItems,
    ...(orgs.length > 0
      ? ([{ text: "Organizations", icon: null, path: null }] as NavItem[]).concat(
          orgs.map((o) => ({
            text: o.name,
            icon: <BusinessOutlined />,
            path: `/orgs/${o.slug}/settings`,
          }))
        )
      : []),
  ];

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.primary.dark,
              backgroundColor: theme.palette.background.paper,
              boxSixing: "border-box",
              borderWidth: isNonMobile ? 0 : "2px",
              width: drawerWidth,
            },
          }}
        >
          <Box width="100%">
            <Box m="1.5rem 2rem 2rem 3rem">
              <FlexBetween color={theme.palette.secondary.main}>
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <Typography variant="h4" fontWeight="bold">
                    AnnoGram
                  </Typography>
                </Box>
                {!isNonMobile && (
                  <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>
            {/*            {!isNonMobile && (
              <Box
                sx={{
                  m: "0 0.5rem 0 0.5rem",
                  bgcolor: theme.palette.background.default,
                  borderRadius: "5px",
                  gap: "3rem",
                  p: "0.1rem 1rem",
                }}
              >
                <InputBase placeholder="Search" sx={{ m: "0 0.5rem" }} />
                <IconButton>
                  <Search />
                </IconButton>
              </Box>
            )}*/}
            <List>
              {navItems.map(({ text, icon, path }) => {
                if (!icon || !path) {
                  return (
                    <Typography
                      key={text}
                      sx={{
                        m: "2.25rem 0 1rem 3rem",
                        color: theme.palette.primary.contrastText,
                        fontSize: "1rem",
                      }}
                    >
                      {text}
                    </Typography>
                  );
                }
                const isActive =
                  active === path ||
                  (path !== "/" && active.startsWith(`${path}/`));

                return (
                  <ListItem key={path} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        navigate(path);
                        setActive(path);
                      }}
                      sx={{
                        backgroundColor: isActive
                          ? theme.palette.secondary.main
                          : "transparent",
                        color: isActive
                          ? theme.palette.primary.main
                          : theme.palette.secondary.main,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          ml: "2rem",
                          color: isActive
                            ? theme.palette.primary.main
                            : theme.palette.secondary.main,
                        }}
                      >
                        {icon}
                      </ListItemIcon>
                      <ListItemText primary={text} />
                      {isActive && <ChevronRightOutlined sx={{ ml: "auto" }} />}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};
